import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Options } from 'components/Options/Options'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import useQueryPagination from 'hooks/useQueryPagination'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Button, Flex, MainHeadline, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import { receiptsOptions } from './receiptsData'
import { approveInventoryReceipt, clearReceipts, getInventoryReceipts, rejectInventoryReceipt } from 'features/inventory/inventoryReceiptsSlice'
import { useNavigate } from 'react-router-dom'
import { setConfirmModal } from 'features/global/globalSlice'
import { FilterEnum } from 'components/Filter/SingleFilter'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { IStatusPill } from 'types/ui/statusPill.interface'

const getReceiptStatusTone = (status: number): IStatusPill['status'] => {
    switch (status) {
        case 2:
            return 'success'
        case 1:
            return 'info'
        case 3:
            return 'neutral'
        default:
            return 'neutral'
    }
}

export const InventoryReceipts = () => {
    const { receipts, totalPages } = useSelector((store: RootState) => store.inventoryReceipts)
    const [params, setParams] = useState<any>(getParams({ location }))
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    useQueryPagination({ fetchFunctions: [getInventoryReceipts], cleanUpFunctions: [clearReceipts], params })

    const approveReceipt = (id: number) => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(approveInventoryReceipt(id))
                },
                word: 'approve this receipt',
            }),
        )
    }

    const rejectReceipt = (id: number) => {
        dispatch(
            setConfirmModal({
                isOpen: true,
                onSave: async () => {
                    await dispatch(rejectInventoryReceipt(id))
                },
                word: 'reject this receipt',
            }),
        )
    }

    const filters = [
        {
            type: FilterEnum.SELECT,
            name: 'Status',
            options: receiptsOptions,
            propName: 'status'
        },
    ]

    const columns: IHonestTableColumn<any>[] = [
        { id: 'supplier', header: 'SUPPLIER', cell: (item) => item.supplier?.name || '/' },
        { id: 'location', header: 'LOCATION', cell: (item) => item.location?.name || '/' },
        { id: 'receiptNo', header: 'RECEIPT NUMBER', cell: (item) => item.receiptNo || '/' },
        { id: 'receiptDate', header: 'RECEIPT DATE', cell: (item) => formatDateForDisplay(item.receiptDate) },
        {
            id: 'status',
            header: 'RECEIPT STATUS',
            cell: (item) => (
                <StatusPill status={getReceiptStatusTone(item.status)}>
                    {receiptsOptions.find(o => o.value == item.status)?.label}
                </StatusPill>
            ),
        },
        { id: 'currency', header: 'RECEIPT CURRENCY', cell: (item) => item.currency || '/' },
        { id: 'amount', header: 'AMOUNT', cell: (item) => item.totalAmount || '/' },
        {
            id: 'actions',
            header: '',
            align: 'right',
            cell: (item) => (
                <span onClick={(e) => e.stopPropagation()}>
                    <Options openModal={(approve: boolean) => approve ? approveReceipt(item.id) : rejectReceipt(item.id)} item={item} title='Receipts' />
                </span>
            ),
        },
    ]

    return <>
        <NavBar enableTemporaryManualClosing input setParams={setParams} placeholder='Search receipts...' filterOptions={filters} visibleFilterOverflow />
        <PageWrapper>
            <Flex between center mb='16' >
                <MainHeadline>Receipts</MainHeadline>
                <Flex gap='1rem' >
                    <Button orange onClick={() => navigate('add')} >
                        Add Receipt
                    </Button>
                </Flex>

            </Flex>
            {!receipts.length ? (
                <NoItems title='Receipts' />
            ) : (
                <HonestTable
                    columns={columns}
                    data={receipts}
                    keyField='id'
                    onClick={(item) => navigate(item.id.toString())}
                />
            )}
        </PageWrapper>
        <PageFooter>
            <Pagination
                setPage={(page) => handlePagination(page, setParams, 30)}
                pageCount={Math.ceil(totalPages)}
                forcePage={(params.page ? params.page : 1) - 1}
            />
        </PageFooter>
    </>
}